import React from 'react'

export interface GridProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Number of columns
   */
  cols?: 1 | 2 | 3 | 4 | 5 | 6 | 12
  /**
   * Responsive column configuration
   */
  responsive?: {
    sm?: 1 | 2 | 3 | 4 | 5 | 6 | 12
    md?: 1 | 2 | 3 | 4 | 5 | 6 | 12
    lg?: 1 | 2 | 3 | 4 | 5 | 6 | 12
    xl?: 1 | 2 | 3 | 4 | 5 | 6 | 12
  }
  /**
   * Gap between grid items
   */
  gap?: 'none' | 'sm' | 'md' | 'lg' | 'xl'
  /**
   * Grid content
   */
  children: React.ReactNode
}

const Grid = React.forwardRef<HTMLDivElement, GridProps>(
  (
    {
      cols = 1,
      responsive,
      gap = 'md',
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    const baseClasses = [
      'grid'
    ]

    const getColClasses = (cols: number) => {
      const colMap: Record<number, string> = {
        1: 'grid-cols-1',
        2: 'grid-cols-2',
        3: 'grid-cols-3',
        4: 'grid-cols-4',
        5: 'grid-cols-5',
        6: 'grid-cols-6',
        12: 'grid-cols-12'
      }
      return colMap[cols] || 'grid-cols-1'
    }

    const gapClasses = {
      none: [],
      sm: ['gap-2'],
      md: ['gap-4'],
      lg: ['gap-6'],
      xl: ['gap-8']
    }

    const responsiveClasses = responsive ? [
      getColClasses(cols),
      responsive.sm && `sm:${getColClasses(responsive.sm)}`,
      responsive.md && `md:${getColClasses(responsive.md)}`,
      responsive.lg && `lg:${getColClasses(responsive.lg)}`,
      responsive.xl && `xl:${getColClasses(responsive.xl)}`
    ].filter(Boolean) : [getColClasses(cols)]

    const gridClasses = [
      ...baseClasses,
      ...responsiveClasses,
      ...gapClasses[gap],
      className
    ].join(' ')

    return (
      <div
        ref={ref}
        className={gridClasses}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Grid.displayName = 'Grid'

export { Grid }
